/*
 * pdp-bag-upsell.js
 * Checkbox-driven bag upsell. Toggling the checkbox immediately adds the
 * matching bag to the cart (or removes it if it's there). Independent of
 * the main Add to Cart button.
 *
 * SINGLE mode: one bag product, optional <select> for its variants (scents).
 * MULTI mode:  a count toggle (radio group) across two or more bag products
 *              (e.g. 240-count vs 440-count, which are separate products).
 *              Each product may also carry a scent <select>; only the active
 *              product's scent select is shown. Selecting a count swaps which
 *              product/variant the checkbox targets, and swaps the cart line
 *              across products when checked.
 */
(function () {
  'use strict';

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchCart() {
    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(new CustomEvent('theme:cart:change', {
      detail: { cart: cart },
      bubbles: true
    }));
  }

  function findLineByVariant(cart, variantId) {
    var id = Number(variantId);
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === id) return cart.items[i];
    }
    return null;
  }

  function numericId(gidOrId) {
    if (gidOrId == null) return null;
    var m = String(gidOrId).match(/(\d+)\s*$/);
    return m ? Number(m[1]) : null;
  }

  // Find any line whose variant belongs to the given bag product. Used when
  // a variant swap needs to remove the old line (regardless of which variant
  // was previously selected).
  function findLineByProduct(cart, productGid) {
    var pid = numericId(productGid);
    if (!pid) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === pid) return cart.items[i];
    }
    return null;
  }

  // MULTI: candidate product ids live on data-product-ids (comma separated).
  function candidateProductIds(root) {
    var raw = root.getAttribute('data-product-ids');
    if (!raw) {
      var single = root.getAttribute('data-product-id');
      return single ? [numericId(single)] : [];
    }
    return raw.split(',').map(numericId).filter(Boolean);
  }

  // Find any cart line matching one of the candidate bag products.
  function findLineByAnyProduct(cart, ids) {
    for (var i = 0; i < cart.items.length; i++) {
      if (ids.indexOf(cart.items[i].product_id) !== -1) return cart.items[i];
    }
    return null;
  }

  function isMulti(root) {
    return root.classList.contains('pdp-bag-upsell--multi');
  }

  function getUpsellRoot(el) {
    return el.closest('[data-pdp-bag-upsell], [data-cart-bag-upsell]');
  }

  function getCheckbox(root) {
    return root && root.querySelector('[data-pdp-bag-upsell-input]');
  }

  // SINGLE mode select (single <select> anywhere in the root).
  function getSelect(root) {
    return root && root.querySelector('[data-pdp-bag-upsell-variant-select]:not([data-for-product])');
  }

  // MULTI: the active product's scent select (may be null if the active
  // product has only one variant).
  function getActiveSelect(root) {
    var pid = root.getAttribute('data-active-product-id');
    if (!pid) return null;
    return root.querySelector('[data-pdp-bag-upsell-variant-select][data-for-product="' + pid + '"]');
  }

  function getActiveCountRadio(root) {
    var pid = root.getAttribute('data-active-product-id');
    if (!pid) return null;
    return root.querySelector('[data-pdp-bag-upsell-count][value="' + pid + '"]');
  }

  // Resolve the currently-selected pricing/variant source for the root,
  // working for both SINGLE and MULTI modes.
  function activeData(root) {
    if (isMulti(root)) {
      var sel = getActiveSelect(root);
      if (sel && sel.options.length) {
        var opt = sel.options[sel.selectedIndex];
        return {
          variantId: opt.value,
          price: opt.getAttribute('data-price'),
          compare: opt.getAttribute('data-compare'),
          savings: opt.getAttribute('data-savings'),
          image: opt.getAttribute('data-image')
        };
      }
      var radio = getActiveCountRadio(root);
      if (radio) {
        return {
          variantId: radio.getAttribute('data-default-variant-id'),
          price: radio.getAttribute('data-price'),
          compare: radio.getAttribute('data-compare'),
          savings: radio.getAttribute('data-savings'),
          image: radio.getAttribute('data-image')
        };
      }
    }
    var s = getSelect(root);
    if (s && s.options.length) {
      var o = s.options[s.selectedIndex];
      return {
        variantId: o.value,
        price: o.getAttribute('data-price'),
        compare: o.getAttribute('data-compare'),
        savings: o.getAttribute('data-savings'),
        image: o.getAttribute('data-image')
      };
    }
    var cb = getCheckbox(root);
    return { variantId: cb && cb.getAttribute('data-variant-id') };
  }

  function currentVariantId(root) {
    return activeData(root).variantId;
  }

  function setBusy(root, busy) {
    if (!root) return;
    root.classList.toggle('pdp-bag-upsell--busy', busy);
    var cb = getCheckbox(root);
    if (cb) cb.disabled = busy;
    root.querySelectorAll('[data-pdp-bag-upsell-variant-select], [data-pdp-bag-upsell-count]').forEach(function (el) {
      el.disabled = busy;
    });
  }

  // Update visible price / compare / savings / image from a data source.
  function applyDisplay(root, data) {
    if (!data) return;
    var now = root.querySelector('[data-pdp-bag-upsell-now]');
    var was = root.querySelector('[data-pdp-bag-upsell-was]');
    var badge = root.querySelector('[data-pdp-bag-upsell-badge]');
    var img = root.querySelector('[data-pdp-bag-upsell-image]');
    var savingsLabel = (badge && badge.getAttribute('data-label')) || 'SAVE';
    if (now && data.price) now.innerHTML = data.price;
    if (was) {
      if (data.compare) { was.innerHTML = data.compare; was.hidden = false; }
      else { was.hidden = true; }
    }
    if (badge) {
      if (data.savings) { badge.innerHTML = savingsLabel + '&nbsp;' + data.savings; badge.hidden = false; }
      else { badge.hidden = true; }
    }
    if (img && data.image) img.setAttribute('src', data.image);
  }

  function updateDisplay(root) {
    applyDisplay(root, activeData(root));
  }

  // MULTI: update the count-aware headline and the selected-bag detail line.
  function updateMeta(root) {
    if (!isMulti(root)) return;
    var radio = getActiveCountRadio(root);
    if (!radio) return;
    var h = root.querySelector('[data-pdp-bag-upsell-headline]');
    var s = root.querySelector('[data-pdp-bag-upsell-subheadline]');
    var p = root.querySelector('[data-pdp-bag-upsell-product]');
    var hv = radio.getAttribute('data-headline');
    var sv = radio.getAttribute('data-subheadline');
    var tv = radio.getAttribute('data-title');
    if (h && hv) h.textContent = hv;
    if (s && sv) s.textContent = sv;
    if (p && tv) p.textContent = tv;
  }

  // MULTI: show the active product's scent row, hide the others.
  function syncScentVisibility(root) {
    var pid = root.getAttribute('data-active-product-id');
    root.querySelectorAll('[data-pdp-bag-upsell-scent-row]').forEach(function (row) {
      row.hidden = row.getAttribute('data-for-product') !== pid;
    });
  }

  function postAdd(variantId) {
    return fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return r.json(); });
  }

  function postChange(lineKey, qty) {
    return fetch(rootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    }).then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return r.json(); });
  }

  function addBag(root) {
    var variantId = currentVariantId(root);
    if (!variantId) return;
    setBusy(root, true);
    postAdd(variantId)
      .then(fetchCart)
      .then(dispatchCartChange)
      .catch(function (err) {
        console.error('[pdp-bag-upsell] add failed', err);
        var cb = getCheckbox(root);
        if (cb) cb.checked = false;
      })
      .finally(function () { setBusy(root, false); });
  }

  function removeBag(root) {
    var ids = candidateProductIds(root);
    setBusy(root, true);
    fetchCart()
      .then(function (cart) {
        // Prefer matching by any candidate product so a stale variant id
        // doesn't strand a line (esp. after a cross-product count swap).
        var line = findLineByAnyProduct(cart, ids) || findLineByVariant(cart, currentVariantId(root));
        if (!line) return cart;
        return postChange(line.key, 0);
      })
      .then(dispatchCartChange)
      .catch(function (err) {
        console.error('[pdp-bag-upsell] remove failed', err);
        var cb = getCheckbox(root);
        if (cb) cb.checked = true;
      })
      .finally(function () { setBusy(root, false); });
  }

  // Swap the cart to the currently-selected variant, removing whichever
  // candidate bag line is present (handles both scent and count swaps).
  function swapToCurrent(root) {
    var ids = candidateProductIds(root);
    var newVariantId = currentVariantId(root);
    if (!newVariantId) return;
    setBusy(root, true);
    fetchCart()
      .then(function (cart) {
        var oldLine = findLineByAnyProduct(cart, ids);
        if (oldLine) {
          return postChange(oldLine.key, 0).then(function () { return postAdd(newVariantId); });
        }
        return postAdd(newVariantId);
      })
      .then(fetchCart)
      .then(dispatchCartChange)
      .catch(function (err) {
        console.error('[pdp-bag-upsell] swap failed', err);
      })
      .finally(function () { setBusy(root, false); });
  }

  // Checkbox toggled → add or remove the currently selected variant.
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target.matches) return;

    if (target.matches('[data-pdp-bag-upsell-input]')) {
      var root = getUpsellRoot(target);
      if (!root) return;
      target.setAttribute('data-variant-id', currentVariantId(root) || '');
      if (target.checked) addBag(root); else removeBag(root);
      return;
    }

    // MULTI: count toggle changed → switch active product, swap scent select,
    // update price/image, and swap the cart line if checked.
    if (target.matches('[data-pdp-bag-upsell-count]')) {
      var croot = getUpsellRoot(target);
      if (!croot) return;
      croot.setAttribute('data-active-product-id', target.value);
      syncScentVisibility(croot);
      updateDisplay(croot);
      updateMeta(croot);
      var ccb = getCheckbox(croot);
      var cvid = currentVariantId(croot);
      if (ccb) ccb.setAttribute('data-variant-id', cvid || '');
      if (ccb && ccb.checked) swapToCurrent(croot);
      return;
    }

    // Scent select changed (SINGLE or MULTI).
    if (target.matches('[data-pdp-bag-upsell-variant-select]')) {
      var sroot = getUpsellRoot(target);
      if (!sroot) return;
      updateDisplay(sroot);
      var scb = getCheckbox(sroot);
      var svid = currentVariantId(sroot);
      if (scb) scb.setAttribute('data-variant-id', svid || '');
      if (scb && scb.checked) swapToCurrent(sroot);
    }
  });

  // Clicks inside the count toggle / scent select should NOT toggle the card.
  document.addEventListener('click', function (e) {
    if (e.target.matches && e.target.matches(
      '[data-pdp-bag-upsell-variant-select], [data-pdp-bag-upsell-variant-select] *,' +
      '[data-pdp-bag-upsell-count], .pdp-bag-upsell__count-btn'
    )) {
      e.stopPropagation();
    }
  });

  // On load: sync checkbox state against the cart. If any candidate bag
  // product is in the cart, mark checked and select the matching count/scent.
  function syncFromCart() {
    var roots = document.querySelectorAll('[data-pdp-bag-upsell], [data-cart-bag-upsell]');
    if (!roots.length) return;
    fetchCart().then(function (cart) {
      roots.forEach(function (root) {
        var ids = candidateProductIds(root);
        var line = findLineByAnyProduct(cart, ids);
        var cb = getCheckbox(root);
        if (cb) cb.checked = !!line;
        if (!line) return;

        if (isMulti(root)) {
          // Point the active product at the product actually in the cart.
          var radio = root.querySelector('[data-pdp-bag-upsell-count][data-product-id="' + line.product_id + '"]');
          if (radio) {
            radio.checked = true;
            root.setAttribute('data-active-product-id', radio.value);
            syncScentVisibility(root);
          }
        }

        // Select the matching scent option, if a select is present.
        var select = isMulti(root) ? getActiveSelect(root) : getSelect(root);
        if (select) {
          for (var i = 0; i < select.options.length; i++) {
            if (Number(select.options[i].value) === line.variant_id) {
              select.selectedIndex = i;
              break;
            }
          }
        }
        updateDisplay(root);
        updateMeta(root);
        if (cb) cb.setAttribute('data-variant-id', String(line.variant_id));
      });
    }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromCart);
  } else {
    syncFromCart();
  }
  document.addEventListener('shopify:section:load', syncFromCart);
})();
